import { useState, useEffect, useMemo, useCallback } from 'react';
import { ArrowRight, Calculator, RefreshCw, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import InputSection from '../components/bundlecalc/InputSection';
import ResultsSection from '../components/bundlecalc/ResultsSection';
import DistributionChart from '../components/bundlecalc/DistributionChart';
import PriceSection from '../components/bundlecalc/PriceSection';
import WalletBreakdown from '../components/bundlecalc/WalletBreakdown';
import {
  CHAIN_LIMITS, FALLBACK_DEXES,
  calculateBundleResults, calculateRequiredWallets,
  fetchBundleChains, fetchBundlePrices,
} from '../utils/bundleCalc';
import { copyToClipboard } from '../lib/utils';
import { bundleGetPoolReserves } from '../lib/api';

const CHAIN_ORDER = [
  'ETH', 'SOL', 'BNB', 'BASE', 'KROWN', 'ROBINHOOD',
  'ARBITRUM', 'OPTIMISM', 'POLYGON', 'AVALANCHE',
  'LINEA', 'SCROLL', 'BLAST', 'ZKSYNC', 'MANTLE',
];
const STORAGE_KEY = 'taiboku_bundlecalc_v1';

const DEFAULT_INPUTS = {
  totalSupply: 1_000_000_000,
  bundlePercentage: 80,
  initialLiquidity: 2,
  baseCurrencyPrice: 3000,
  taxRate: 0,
  dexFee: 0.30,
  chain: 'BASE',
  dex: 'uniswap_v3',
  walletCount: 10,
  maxWalletPercent: 0,
  baseCurrency: 'NATIVE',
};

const chainAccentClass = (chain) => {
  const map = {
    ETH: 'bg-blue-500/15 text-blue-400',
    SOL: 'bg-purple-500/15 text-purple-400',
    BNB: 'bg-yellow-500/15 text-yellow-400',
    BASE: 'bg-indigo-500/15 text-indigo-400',
    KROWN: 'bg-amber-500/15 text-amber-400',
    ROBINHOOD: 'bg-emerald-500/15 text-emerald-400',
    ARBITRUM: 'bg-sky-500/15 text-sky-400',
    OPTIMISM: 'bg-red-500/15 text-red-400',
    POLYGON: 'bg-violet-500/15 text-violet-400',
    AVALANCHE: 'bg-rose-500/15 text-rose-400',
    LINEA: 'bg-cyan-500/15 text-cyan-400',
    SCROLL: 'bg-orange-500/15 text-orange-400',
    BLAST: 'bg-lime-500/15 text-lime-400',
    ZKSYNC: 'bg-blue-500/15 text-blue-300',
    MANTLE: 'bg-teal-500/15 text-teal-400',
  };
  return map[chain] || 'bg-muted text-muted-foreground';
};

export default function BundleCalcPage() {
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [dexRegistry, setDexRegistry] = useState(FALLBACK_DEXES);
  const [livePrices, setLivePrices] = useState(null);
  const [priceStatus, setPriceStatus] = useState('loading');

  // Live price polling — 60 s (matches source repo cadence)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setPriceStatus('loading');
      try {
        const data = await fetchBundlePrices();
        if (cancelled) return;
        setLivePrices(data.prices);
        setPriceStatus('ok');
        setInputs(prev => {
          if (prev.baseCurrency === 'USDG') return prev;
          const p = data.prices?.[prev.chain]?.usd;
          if (p && Math.abs(p - prev.baseCurrencyPrice) > 0.001) {
            return { ...prev, baseCurrencyPrice: p };
          }
          return prev;
        });
      } catch (err) {
        if (!cancelled) setPriceStatus('error');
      }
    };
    load();
    const iv = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // Fetch the chain/DEX registry once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchBundleChains();
        if (cancelled) return;
        const reg = {};
        (data.chains || []).forEach(c => { reg[c.id] = c.dexes || []; });
        if (Object.keys(reg).length) setDexRegistry(reg);
      } catch { /* keep fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const results = useMemo(() => calculateBundleResults(inputs), [inputs]);
  const currentDexes = dexRegistry[inputs.chain] || [];

  const handleInputChange = useCallback((name, value) => {
    const numValue = typeof value === 'string' ? (parseFloat(value) || 0) : value;
    setInputs(prev => {
      const next = { ...prev, [name]: numValue };
      // Bundle % auto-cap logic (from original TS source): when the max-wallet
      // cap is on, keep bundle % ≤ what fits inside the chain's wallet limit.
      const chainLimit = CHAIN_LIMITS[next.chain].maxWallets;
      if ((name === 'maxWalletPercent' || (name === 'bundlePercentage' && next.maxWalletPercent > 0) ||
           (name === 'totalSupply' && next.maxWalletPercent > 0)) && next.maxWalletPercent > 0) {
        const required = calculateRequiredWallets(next.totalSupply, next.bundlePercentage, next.maxWalletPercent);
        if (required > chainLimit) {
          const maxBundle = chainLimit * next.maxWalletPercent;
          next.bundlePercentage = Math.floor(maxBundle * 10) / 10;
        }
      }
      return next;
    });
  }, []);

  const handleChainChange = useCallback((chain) => {
    setInputs(prev => {
      const chainMeta = CHAIN_LIMITS[chain];
      const dexList = dexRegistry[chain] || [];
      const firstDex = dexList[0];
      const p = livePrices?.[chain]?.usd || chainMeta.defaultPrice;
      const next = {
        ...prev,
        chain,
        baseCurrency: 'NATIVE',
        baseCurrencyPrice: p,
        dex: firstDex?.id || '',
        dexFee: firstDex?.default_fee ?? 0.30,
        walletCount: Math.min(prev.walletCount, chainMeta.maxWallets),
      };
      if (next.maxWalletPercent > 0) {
        const required = calculateRequiredWallets(next.totalSupply, next.bundlePercentage, next.maxWalletPercent);
        if (required > chainMeta.maxWallets) {
          const maxBundle = chainMeta.maxWallets * next.maxWalletPercent;
          next.bundlePercentage = Math.floor(maxBundle * 10) / 10;
        }
      }
      return next;
    });
  }, [dexRegistry, livePrices]);

  const handleDexChange = useCallback((dexId) => {
    setInputs(prev => {
      const d = (dexRegistry[prev.chain] || []).find(x => x.id === dexId);
      return { ...prev, dex: dexId, dexFee: d?.default_fee ?? prev.dexFee };
    });
  }, [dexRegistry]);

  const handleReset = () => setInputs(DEFAULT_INPUTS);

  const handleBaseCurrencyChange = useCallback((cur) => {
    setInputs(prev => ({
      ...prev,
      baseCurrency: cur,
      baseCurrencyPrice: cur === 'USDG'
        ? 1
        : (livePrices?.[prev.chain]?.usd || CHAIN_LIMITS[prev.chain].defaultPrice),
    }));
  }, [livePrices]);

  const [poolLoading, setPoolLoading] = useState(false);
  const handleLoadPool = useCallback(async (poolAddr) => {
    setPoolLoading(true);
    try {
      const d = await bundleGetPoolReserves(inputs.chain, poolAddr.trim());
      const isUsdg = (d.base?.symbol || '').toUpperCase() === 'USDG';
      setInputs(prev => ({
        ...prev,
        totalSupply: Math.round(d.token.reserve),
        initialLiquidity: d.base.reserve,
        baseCurrencyPrice: d.base.usd_price || prev.baseCurrencyPrice,
        baseCurrency: isUsdg ? 'USDG' : 'NATIVE',
      }));
      toast.success(
        `Loaded ${d.token.symbol}/${d.base.symbol} pool (${d.source}): ` +
        `${Math.round(d.token.reserve).toLocaleString()} ${d.token.symbol} · ` +
        `${d.base.reserve.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${d.base.symbol}`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not read pool reserves');
    } finally {
      setPoolLoading(false);
    }
  }, [inputs.chain]);

  const saveConfig = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(inputs));
      toast.success('Preset saved to this browser');
    } catch { toast.error('Could not save preset'); }
  };
  const loadConfig = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) { toast.info('No saved preset in this browser'); return; }
      const cfg = JSON.parse(raw);
      setInputs(cfg);
      toast.success('Preset loaded');
    } catch { toast.error('Could not load preset'); }
  };

  const copyShareLink = async () => {
    // Compact Telegram-friendly share args
    const args = `/bundle ${inputs.totalSupply} ${inputs.bundlePercentage}% ${inputs.initialLiquidity} ${inputs.chain.toLowerCase()} ${inputs.dex}${inputs.taxRate > 0 ? ` tax=${inputs.taxRate}` : ''}${inputs.maxWalletPercent > 0 ? ` max=${inputs.maxWalletPercent}` : ''}`;
    try {
      await copyToClipboard(args);
      toast.success('Telegram command copied — paste it in the bot chat');
    } catch { toast.error('Copy failed'); }
  };

  return (
    <div className="space-y-6">
      {/* Hero */}
      <section className="text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/25 text-xs font-medium text-primary mb-3">
          <Calculator className="w-3.5 h-3.5" />
          Taiboku Bundle Calc
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-3">
          <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-emerald-400 bg-clip-text text-transparent">
            Crypto Bundle Launch Calculator
          </span>
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto mb-4">
          Plan a token launch with forensic precision. Compute bundle cost, price impact,
          per-wallet distribution and FDV across every major chain and DEX.
        </p>
        <div className="flex items-center justify-center gap-1.5 flex-wrap text-[11px] sm:text-xs">
          {CHAIN_ORDER.map((c, i) => (
            <span key={c} className="flex items-center gap-1.5">
              <span className={`px-2 py-0.5 rounded-full ${chainAccentClass(c)}`}>{CHAIN_LIMITS[c].name}</span>
              {i < CHAIN_ORDER.length - 1 && <ArrowRight className="w-3 h-3 text-muted-foreground/60" />}
            </span>
          ))}
        </div>
        <div className="flex items-center justify-center gap-2 mt-4 text-[11px] text-muted-foreground">
          <span className={`inline-flex items-center gap-1.5 ${priceStatus === 'ok' ? 'text-emerald-500' : priceStatus === 'error' ? 'text-red-500' : ''}`}>
            <RefreshCw className={`w-3 h-3 ${priceStatus === 'loading' ? 'animate-spin' : ''}`} />
            {priceStatus === 'ok' ? 'Live prices ✓' : priceStatus === 'error' ? 'Live prices offline' : 'Fetching prices…'}
          </span>
          <span className="text-border">•</span>
          <button
            onClick={copyShareLink}
            className="inline-flex items-center gap-1.5 hover:text-primary transition-colors"
            data-testid="bundle-copy-tg-btn"
          >
            <Copy className="w-3 h-3" />
            Copy Telegram command
          </button>
        </div>
      </section>

      {/* Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-1">
          <InputSection
            inputs={inputs}
            dexes={currentDexes}
            onInputChange={handleInputChange}
            onChainChange={handleChainChange}
            onDexChange={handleDexChange}
            onBaseCurrencyChange={handleBaseCurrencyChange}
            onLoadPool={handleLoadPool}
            poolLoading={poolLoading}
            onReset={handleReset}
            onSave={saveConfig}
            onLoad={loadConfig}
            results={results}
          />
        </div>

        <div className="xl:col-span-2 space-y-6" data-testid="bundle-results-panel">
          {results && (
            <>
              <ResultsSection
                results={results}
                chain={inputs.chain}
                initialLiquidity={inputs.initialLiquidity}
                baseCurrencyPrice={inputs.baseCurrencyPrice}
                baseSymbol={inputs.baseCurrency === 'USDG' ? 'USDG' : undefined}
              />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <DistributionChart
                  initialPoolPercentage={inputs.bundlePercentage}
                  finalPoolPercentage={100 - inputs.bundlePercentage}
                />
                <PriceSection
                  initialPrice={results.initialTokenPriceUsd}
                  finalPrice={results.finalTokenPriceUsd}
                  priceIncrease={results.priceIncreaseMultiple}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {results && results.walletDistribution.length > 0 && (
        <WalletBreakdown wallets={results.walletDistribution} chain={inputs.chain} baseSymbol={inputs.baseCurrency === 'USDG' ? 'USDG' : undefined} />
      )}

      {/* Legal disclaimer — same tone as original repo */}
      <div className="text-center text-[11px] text-muted-foreground/70 max-w-2xl mx-auto py-4 border-t border-border">
        Educational tool. Verify all numbers on-chain before launching. Bundling behaviour
        may violate the terms of some launchpads and token contracts — always DYOR.
      </div>
    </div>
  );
}
